import type React from 'react';
import {Box, IconButton, Paper, Stack, Tooltip, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {SimpleTreeView} from '@mui/x-tree-view/SimpleTreeView';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {COLORS} from '~/style/colors-ts';
import {branchPaths, composition, findNode, labelsOf, scopeChildren, topShare} from '../domain/exploreTree';
import type {Explore, ExploreNode} from '../types/categoryProfileTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const SEGMENT_COLORS = ['#2D6FF5', '#6B4FD8', '#0F7B7B', '#A15C00', '#7A7F8A', '#C6CAD4'];

type Props = {
    data: Explore;
    selected: string;
    onSelect: (path: string) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    money: (v: number) => string;
};

function TreeNodes({nodes, money}: {nodes: ExploreNode[], money: (v: number) => string}) {
    return nodes.map(n => (
        <TreeItem key={n.path} itemId={n.path} label={
            <Stack direction="row" justifyContent="space-between" gap={1}>
                <Typography variant="body2" noWrap title={n.name} sx={{minWidth: 0}}>{n.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{flexShrink: 0}}>{money(n.spend)}</Typography>
            </Stack>
        }>
            {n.children.length > 0 && <TreeNodes nodes={n.children} money={money}/>}
        </TreeItem>
    ));
}

/** Left panel of Explore Categories: scope card, taxonomy tree and composition insights (spec 3.2). */
export const CategoryNavigator: React.FC<Props> = ({data, selected, onSelect, collapsed, onToggleCollapsed, money}) => {
    if (collapsed) {
        return (
            <Box sx={{pt: 1, width: 20, flexShrink: 0}}>
                <Tooltip title="Show categories" placement="right">
                    <IconButton size="small" onClick={onToggleCollapsed} aria-label="Show categories" sx={{p: 0, ml: -0.5}}>
                        <ChevronRightIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }
    const children = scopeChildren(data.tree, selected);
    const selectedNode = selected ? findNode(data.tree, selected) : undefined;
    const parentLabels = labelsOf(selected).slice(0, -1);
    const parent = parentLabels.length ? findNode(data.tree, parentLabels.join('|')) : undefined;
    const spend = selectedNode?.spend ?? data.kpis?.totalSpend ?? 0;
    const suppliers = selectedNode?.suppliers ?? data.kpis?.suppliers ?? 0;
    const transactions = selectedNode?.transactions ?? data.kpis?.transactions ?? 0;
    const segments = composition(children);
    const top3 = topShare(children);

    return (
        <Paper variant="outlined" sx={{width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Stack direction="row" alignItems="flex-start" sx={{p: 2, borderBottom: `1px solid ${COLORS.grey[200]}`}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="overline" color="text.secondary">
                        {selected ? 'Selected category' : 'All categories'}
                    </Typography>
                    <Typography variant="h5">{money(spend)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {`${suppliers.toLocaleString(LOCALE)} suppliers · ${transactions.toLocaleString(LOCALE)} transactions`}
                    </Typography>
                    {selectedNode && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {`${selectedNode.spendSharePct.toFixed(1)}% of ${parent?.name ?? 'all categories'}`}
                        </Typography>
                    )}
                </Box>
                <Tooltip title="Hide categories"><IconButton size="small" onClick={onToggleCollapsed}><ChevronLeftIcon/></IconButton></Tooltip>
            </Stack>

            <Box sx={{flex: 1, overflow: 'auto', px: 1, py: 1}}>
                <SimpleTreeView
                    // Selection is handled here so the chevron only expands or collapses: the tree view would also
                    // select an item when its chevron (inside the item's row) is clicked.
                    selectedItems={selected || null}
                    onSelectedItemsChange={(e, id) => {
                        if (e?.type === 'keydown') onSelect(id === selected ? '' : id ?? '');
                    }}
                    onItemClick={(e, id) => {
                        if ((e.target as HTMLElement).closest('.MuiTreeItem-iconContainer')) return;
                        onSelect(id === selected ? '' : id);
                    }}
                    expansionTrigger="iconContainer"
                    itemChildrenIndentation={20} // wide enough to tell L3 leaves from the L4s above them
                    sx={{
                        // The chevron is its own button (expand / collapse); show that on hover and keyboard focus.
                        '& .MuiTreeItem-iconContainer': {
                            width: 24, height: 24, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'background-color 120ms',
                            '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.14), color: 'primary.main'},
                        },
                        '& .MuiTreeItem-content:has(.MuiTreeItem-iconContainer:hover)': {bgcolor: 'transparent'},
                    }}
                    defaultExpandedItems={branchPaths(data.tree)}
                >
                    <TreeNodes nodes={data.tree} money={money}/>
                </SimpleTreeView>
            </Box>

            <Box sx={{p: 2, borderTop: `1px solid ${COLORS.grey[200]}`}}>
                {children.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Leaf category, no deeper levels</Typography>
                ) : (
                    <Stack gap={1}>
                        <Typography variant="body2">
                            <b>{`Inside ${selectedNode?.name ?? 'all categories'}`}</b>
                            {` · ${children.length} categories`}
                            {top3 !== null && ` · Top 3 = ${top3.toFixed(0)}% of spend`}
                        </Typography>
                        <Stack direction="row" sx={{height: 10, borderRadius: 1, overflow: 'hidden'}}>
                            {segments.map((s, i) => (
                                <Tooltip key={s.key} title={`${s.key}: ${s.sharePct.toFixed(1)}%`}>
                                    <Box
                                        onClick={() => s.path && onSelect(s.path)}
                                        sx={{width: `${s.sharePct}%`, bgcolor: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
                                            cursor: s.path ? 'pointer' : 'default'}}
                                    />
                                </Tooltip>
                            ))}
                        </Stack>
                        {segments.slice(0, 3).map((s, i) => (
                            <Stack key={s.key} direction="row" gap={1} alignItems="center">
                                <Box sx={{width: 8, height: 8, borderRadius: '2px', bgcolor: SEGMENT_COLORS[i]}}/>
                                <Typography variant="caption" sx={{flex: 1}} noWrap>{s.key}</Typography>
                                <Typography variant="caption">{`${s.sharePct.toFixed(1)}%`}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
                    Select a category to scope the table
                </Typography>
            </Box>
        </Paper>
    );
};

